<template>
  <div class="notifications-list">
    <vs-table
      search
      pagination
      ref="table"
      noDataText="Sem dados disponíveis"
      :data="notifications"
      :max-items="itemsPerPage"
    >
      <div slot="header" class="flex flex-wrap-reverse items-center flex-grow justify-between">
        <div class="flex flex-wrap-reverse items-center">
          <div
            v-if="isOriginBackoffice && canSee('add')"
            class="p-3 mb-4 mr-4 rounded-lg cursor-pointer flex items-center justify-betweentext-lg font-medium text-base text-primary border border-solid border-primary"
            @click="openCreate"
          >
            <feather-icon icon="PlusIcon" svgClasses="h-4 w-4" />
            <span class="ml-2 text-base text-primary">Adicionar Novo</span>
          </div>
        </div>

        <vs-dropdown vs-trigger-click class="cursor-pointer mb-4 mr-4">
          <div class="p-4 border border-solid d-theme-border-grey-light rounded-full d-theme-dark-bgcursor-pointer flex items-center justify-between font-medium">
            <span class="mr-2">
              {{ currentPage * itemsPerPage - (itemsPerPage - 1) }} -
              {{notifications.length - currentPage * itemsPerPage > 0 ? currentPage * itemsPerPage : notifications.length }} de {{ queriedItems }}
            </span>
            <feather-icon icon="ChevronDownIcon" svgClasses="h-4 w-4" />
          </div>
          <vs-dropdown-menu>
            <vs-dropdown-item
              v-for="option in perPageOptions"
              :key="option"
              @click="itemsPerPage = option"
            >
              <span>{{ option }}</span>
            </vs-dropdown-item>
          </vs-dropdown-menu>
        </vs-dropdown>
      </div>

      <template slot="thead">
        <vs-th sort-key="title">Título</vs-th>
        <vs-th sort-key="environment">Ambiente</vs-th>
        <vs-th>Segmentação</vs-th>
        <vs-th sort-key="scheduled_at">Agendada</vs-th>
        <vs-th sort-key="is_dispatched">Status</vs-th>
        <vs-th>Criado por</vs-th>
        <vs-th>Ações</vs-th>
      </template>

      <template slot-scope="{data}">
        <tbody>
          <vs-tr :data="tr" :key="indextr" v-for="(tr, indextr) in data">
            <vs-td>
              <p class="product-name font-medium truncate">{{ tr.title }}</p>
            </vs-td>

            <vs-td>
              <p class="product-name font-medium truncate">{{ tr.environment || '-' }}</p>
            </vs-td>

            <vs-td>
              <div class="segmentation-cell">
                <div class="segmentation-cell__row">
                  <p
                    class="product-name font-medium"
                    :class="isSegmentationExpanded(tr.id) ? 'segmentation-text--expanded' : 'segmentation-text--truncate'"
                  >
                    {{ segmentationSummary(tr) }}
                  </p>
                  <button
                    type="button"
                    class="segmentation-cell__toggle"
                    v-if="canExpandSegmentation(tr)"
                    @click.stop="toggleSegmentation(tr)"
                  >
                    <vs-icon :icon="isSegmentationExpanded(tr.id) ? 'expand_less' : 'expand_more'" />
                  </button>
                </div>
                <vs-chip v-if="tr.target_new_pricing_only" color="primary" class="mt-1">Novo Pricing</vs-chip>
              </div>
            </vs-td>

            <vs-td>
              <p class="product-name font-medium truncate">{{ formatDateTime(tr.scheduled_at) }}</p>
            </vs-td>

            <vs-td>
              <div class="flex justify-center gap-2">
                <vs-chip color="success" v-if="tr.is_dispatched">Disparada</vs-chip>
                <vs-chip color="warning" v-else-if="!tr.is_cancelled">Agendada</vs-chip>
                <vs-chip color="danger" v-if="tr.is_cancelled">Cancelada</vs-chip>
              </div>
            </vs-td>

            <vs-td>
              <p class="product-name font-medium truncate">{{ tr.created_by_name || '-' }}</p>
            </vs-td>

            <vs-td class="actionTD">
              <div class="inline-flex gap-2">
                <vs-button
                  v-if="canSee('update') && isOriginBackoffice && tr.is_dispatched"
                  type="border"
                  size="small"
                  @click="openDetails(tr)"
                >
                  Detalhes
                </vs-button>
                <vs-button
                  v-if="canSee('update') && isOriginBackoffice && !tr.is_dispatched"
                  type="border"
                  size="small"
                  @click="openEdit(tr)"
                >
                  Editar
                </vs-button>
                <vs-button
                  v-if="canSee('update') && isOriginBackoffice && !tr.is_dispatched"
                  type="filled"
                  size="small"
                  :color="tr.is_cancelled ? 'success' : 'danger'"
                  @click="promptCancel(tr)"
                >
                  {{ tr.is_cancelled ? 'Reativar' : 'Cancelar' }}
                </vs-button>
              </div>
            </vs-td>
          </vs-tr>
        </tbody>
      </template>
    </vs-table>

    <vs-popup
      class="feature-notification-popup"
      :active.sync="addDataSideBar"
      :title="popupTitle"
    >
      <div class="feature-notification-form">
        <input ref="imageInput" type="file" accept="image/*,video/*" class="fn-hidden-input" @change="handleImageChange" />
        <input ref="fileInput" type="file" accept=".pdf,.xls,.xlsx,.csv,.txt,.doc,.docx" class="fn-hidden-input" @change="handleFileChange" />

        <div class="fn-field">
          <vs-input
            label="Título"
            v-model="form.title"
            class="w-full"
            name="title"
            v-validate="'required'"
            :disabled="isReadOnly"
          />
          <span class="text-danger text-sm" v-show="errors.has('title')">{{ errors.first('title') }}</span>
        </div>

        <div class="fn-field">
          <vs-textarea
            label="Texto"
            v-model="form.text"
            class="w-full"
            name="text"
            v-validate="'required'"
            :disabled="isReadOnly"
          />
          <span class="text-danger text-sm" v-show="errors.has('text')">{{ errors.first('text') }}</span>
        </div>

        <div class="fn-field">
          <vs-input disabled label="Ambiente" v-model="form.environment" class="w-full" />
        </div>

        <div class="fn-row">
          <div class="fn-field fn-col">
            <label class="fn-label">Imagem ou vídeo</label>
            <div class="fn-media-cell">
              <vs-button
                v-if="!form.video_url"
                type="border"
                icon-pack="feather"
                icon="icon-image"
                :disabled="isUploadingImage || isReadOnly"
                @click="triggerInput('imageInput')"
              >
                {{ isUploadingImage ? 'Enviando...' : 'Selecionar mídia' }}
              </vs-button>
              <div v-else-if="isImageMedia(form.video_url)" class="fn-media-preview">
                <img :src="form.video_url" alt="pré-visualização" />
                <div v-if="!isReadOnly" class="fn-media-actions">
                  <vs-button type="border" size="small" @click="triggerInput('imageInput')">Trocar</vs-button>
                  <vs-button type="flat" size="small" color="danger" icon-pack="feather" icon="icon-x" @click="clearMedia" />
                </div>
              </div>
              <div v-else class="fn-media-file">
                <feather-icon icon="VideoIcon" svgClasses="h-5 w-5 text-primary" />
                <a :href="form.video_url" target="_blank" rel="noopener noreferrer" class="fn-media-filename">
                  Vídeo carregado
                </a>
                <template v-if="!isReadOnly">
                  <vs-button type="border" size="small" @click="triggerInput('imageInput')">Trocar</vs-button>
                  <vs-button type="flat" size="small" color="danger" icon-pack="feather" icon="icon-x" @click="clearMedia" />
                </template>
              </div>
            </div>
          </div>

          <div class="fn-field fn-col">
            <label class="fn-label">Documento anexado</label>
            <div class="fn-media-cell">
              <vs-button
                v-if="!form.file_url"
                type="border"
                icon-pack="feather"
                icon="icon-paperclip"
                :disabled="isUploadingFile || isReadOnly"
                @click="triggerInput('fileInput')"
              >
                {{ isUploadingFile ? 'Enviando...' : 'Selecionar documento' }}
              </vs-button>
              <div v-else class="fn-media-file">
                <feather-icon icon="FileIcon" svgClasses="h-5 w-5 text-primary" />
                <a :href="form.file_url" target="_blank" rel="noopener noreferrer" class="fn-media-filename">
                  {{ form.file_name || 'Arquivo enviado' }}
                </a>
                <vs-button v-if="!isReadOnly" type="flat" size="small" color="danger" icon-pack="feather" icon="icon-x" @click="clearFile" />
              </div>
            </div>
          </div>
        </div>

        <div class="fn-divider" />

        <div class="fn-field">
          <label class="fn-label">Destinatários</label>
          <div class="fn-audience">
            <label
              v-for="option in audienceOptions"
              :key="option.value"
              class="fn-audience__option"
              :class="{
                'fn-audience__option--active': audienceMode === option.value,
                'fn-audience__option--disabled': isReadOnly,
              }"
            >
              <vs-radio v-model="audienceMode" :vs-value="option.value" :disabled="isReadOnly" />
              <div class="fn-audience__info">
                <span class="fn-audience__title">{{ option.title }}</span>
                <span class="fn-audience__hint">{{ option.hint }}</span>
              </div>
            </label>
          </div>
        </div>

        <div v-if="audienceMode === 'single'" class="fn-field">
          <label class="fn-label">Cliente</label>
          <v-select
            v-model="singleTenantName"
            :options="availableTenantShortNames"
            :clearable="true"
            :reduce="option => option"
            placeholder="Selecione o cliente"
            :disabled="isReadOnly"
          />
        </div>

        <div v-if="audienceMode === 'sheet'" class="fn-field">
          <label class="fn-label">
            A planilha precisa ter uma coluna chamada <code>nome</code> com o nome dos clientes. Máx. {{ maxSheetRows }} linhas.
          </label>
          <ImportExcel
            v-if="!isReadOnly"
            :key="`sheet-${sheetResetKey}`"
            :on-success="onSheetParsed"
            file-name="file"
            :action="sheetUploadEndpoint"
            :headers="uploadHeaders"
            :max-rows-to-import="maxSheetRows"
          />
          <p v-if="sheetTenantNames.length" class="fn-media-hint">
            {{ sheetTenantNames.length }} cliente(s) carregado(s)
            <a v-if="!isReadOnly" class="fn-inline-link" @click="clearSheet">limpar</a>
          </p>
          <p v-if="sheetError" class="fn-error">{{ sheetError }}</p>
        </div>

        <div v-if="audienceMode !== 'single'" class="fn-filter">
          <div class="fn-filter__text">
            <span class="fn-filter__title">Apenas clientes do Novo Pricing</span>
            <span class="fn-filter__hint">Filtra os destinatários acima para incluir apenas clientes com plano contratado</span>
          </div>
          <vs-switch v-model="form.target_new_pricing_only" :disabled="isReadOnly" />
        </div>

        <div class="fn-field">
          <label class="fn-label">Agendado para</label>
          <flat-pickr
            class="w-full"
            v-model="form.scheduled_at"
            :config="scheduleConfigDateTimePicker"
            placeholder="Selecione data e hora"
            :disabled="isReadOnly"
          />
        </div>

        <div v-if="isReadOnly && form.id" class="fn-row">
          <div class="fn-field fn-col">
            <label class="fn-label">Criado por</label>
            <vs-input :value="form.created_by_name || '-'" class="w-full" disabled />
          </div>
          <div class="fn-field fn-col">
            <label class="fn-label">IP do usuário que criou</label>
            <vs-input :value="form.created_by_ip || '-'" class="w-full" disabled />
          </div>
        </div>

        <footer class="fn-footer">
          <vs-button type="border" color="dark" @click="closeForm">{{ isReadOnly ? 'Fechar' : 'Cancelar' }}</vs-button>
          <vs-button
            v-if="!isReadOnly"
            type="filled"
            :disabled="isSaving || isUploadingImage || isUploadingFile"
            @click="submitForm"
          >
            {{ formMode === 'create' ? 'Salvar' : 'Atualizar' }}
          </vs-button>
        </footer>
      </div>
    </vs-popup>

    <vs-popup title="Confirmar Ação" :active.sync="confirmDialog.active">
      <p class="mt-0 pt-0 font-medium">{{ confirmDialog.target && confirmDialog.target.is_cancelled ? 'Reativar' : 'Cancelar' }} a notificação "{{ confirmDialog.target ? confirmDialog.target.title : '' }}"?</p>

      <div class="flex gap-2 mt-3">
        <vs-button type="filled" @click="confirmCancelAction">Confirmar</vs-button>
        <vs-button type="border" color="dark" @click="confirmDialog.active = false">Fechar</vs-button>
      </div>
    </vs-popup>

  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import vSelect from 'vue-select';
import {hasPermissionBackOffice, encodeToBase64} from "@/utils";
import featureNotification from '@/http/requests/featureNotification';
import fileRequests from '@/http/requests/file';
import tenants from '@/http/requests/tenants';
import ImportExcel from '@/components/excel/ImportExcel.vue';
import flatPickr from 'vue-flatpickr-component';
import 'flatpickr/dist/flatpickr.css';

export default {
  name: 'notifications-list',
  components: {
    flatPickr,
    vSelect,
    ImportExcel,
  },
  data() {
    return {
      itemsPerPage: 4,
      perPageOptions: [4, 10, 15, 20],
      notifications: [],
      addDataSideBar: false,
      formMode: 'create',
      form: {
        id: null,
        title: '',
        text: '',
        environment: '',
        video_url: '',
        file_url: '',
        file_name: '',
        scheduled_at: '',
        target_new_pricing_only: false,
      },
      audienceMode: 'all',
      singleTenantName: '',
      sheetTenantNames: [],
      sheetError: '',
      isSaving: false,
      isUploadingImage: false,
      isUploadingFile: false,
      isMounted: false,
      availableTenants: [],
      sheetResetKey: 0,
      maxSheetRows: 1000,
      sheetNameColumns: ['tenant_name', 'tenant', 'tenant_fqdn', 'fqdn', 'nome', 'name'],
      documentExtensions: ['pdf', 'xls', 'xlsx', 'csv', 'txt', 'doc', 'docx'],
      mediaExtensions: ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp', 'mp4', 'mov', 'webm', 'avi'],
      confirmDialog: {
        active: false,
        target: null,
      },
      isReadOnly: false,
      expandedSegmentationIds: [],
      selectedTenantName: '',
      scheduleConfigDateTimePicker: {
        enableTime: true,
        noCalendar: false,
        minDate: null,
        dateFormat: 'd/m/Y H:i',
      },
    }
  },
  computed: {
    ...mapGetters([
      'token',
      'name',
      'email',
      'users',
      'hasLogged',
    ]),
    currentPage() {
      if (this.isMounted && this.$refs.table) { return this.$refs.table.currentx; }
      return 0;
    },
    queriedItems() {
      return this.$refs.table ? this.$refs.table.queriedResults.length : this.notifications.length;
    },
    isOriginBackoffice(){
      return process.env.VUE_APP_ORIGIN_BACKOFFICE && process.env.VUE_APP_ORIGIN_BACKOFFICE === 'true'
    },
    environmentOptions() {
      if (window.location.host.includes('localhost') || window.location.host.includes('testing')){
        return ['development'];
      } else if (window.location.host.includes('beta')){
        return ['beta'];
      } else {
        return ['production'];
      }
    },
    availableTenantFqdns() {
      return this.availableTenants.map(item => item.fqdn).filter(Boolean);
    },
    availableTenantShortNames() {
      return this.availableTenantFqdns.map(fqdn => fqdn.split('.')[0]);
    },
    tenantLookupBySlug() {
      const map = {};
      this.availableTenantFqdns.forEach(fqdn => {
        const slug = fqdn.split('.')[0];
        if (slug) map[slug.toLowerCase()] = fqdn;
      });
      return map;
    },
    availableTenantFqdnSet() {
      return new Set(this.availableTenantFqdns);
    },
    uploadEndpoint() {
      return process.env.VUE_APP_API_BASE_URL + '/api/file/upload';
    },
    sheetUploadEndpoint() {
      const tenant = encodeURIComponent(this.selectedTenantName || '');
      return `${this.uploadEndpoint}?tenantName=${tenant}`;
    },
    uploadHeaders() {
      const accessToken = localStorage.getItem('accessToken') || this.token;
      return { Authorization: 'Bearer ' + accessToken };
    },
    popupTitle() {
      if (this.isReadOnly) return 'Detalhes da Notificação';
      return this.formMode === 'create' ? 'Criar Notificação' : 'Editar Notificação';
    },
    audienceOptions() {
      return [
        { value: 'all', title: 'Todos os clientes', hint: 'Envia para todos os clientes ativos do ambiente' },
        { value: 'single', title: 'Cliente único', hint: 'Selecione um cliente específico' },
        { value: 'sheet', title: 'Importar planilha', hint: 'Envia para uma lista carregada via planilha' },
      ];
    },
  },
  methods: {
    canSee(word) {
      return hasPermissionBackOffice(`${this.$route.name}-${word}`);
    },
    segmentationSummary(notification) {
      if (!notification) return 'Todos';
      const names = notification.target_tenant_names || [];
      if (!names.length) return 'Todos';
      return names.join(', ');
    },
    canExpandSegmentation(notification) {
      return this.segmentationSummary(notification).length > 40;
    },
    isSegmentationExpanded(notificationId) {
      return this.expandedSegmentationIds.includes(notificationId);
    },
    toggleSegmentation(notification) {
      if (!this.canExpandSegmentation(notification)) return;
      if (this.isSegmentationExpanded(notification.id)) {
        this.expandedSegmentationIds = this.expandedSegmentationIds.filter(id => id !== notification.id);
        return;
      }
      this.expandedSegmentationIds.push(notification.id);
    },
    async loadDefaultTenant() {
      try {
        const tenantsList = await tenants.getAllTenants(this.token, true);
        const activeList = (tenantsList || []).filter(item => item && !item.deleted_at);
        this.availableTenants = activeList;
        if (activeList.length) {
          const activeTenant = activeList[0];
          if (activeTenant && activeTenant.fqdn) {
            this.selectedTenantName = activeTenant.fqdn.split('.')[0];
          }
        }
      } catch (exception) {
        console.error(exception);
        this.selectedTenantName = '';
        this.availableTenants = [];
      }
    },
    openCreate() {
      this.resetForm();
      this.formMode = 'create';
      this.isReadOnly = false;
      this.addDataSideBar = true;
      this.form.environment = this.environmentOptions[0] || '';
    },
    openEdit(notification) {
      this.formMode = 'update';
      this.isReadOnly = false;
      this.form = {
        id: notification.id,
        title: notification.title || '',
        text: notification.text || '',
        environment: notification.environment || '',
        video_url: notification.video_url || '',
        file_url: notification.file_url || '',
        file_name: notification.file_name || '',
        scheduled_at: this.formatDateForInput(notification.scheduled_at),
        target_new_pricing_only: !!notification.target_new_pricing_only,
        created_by_name: notification.created_by_name || '',
        created_by_ip: notification.created_by_ip || '',
      };
      const targetNames = notification.target_tenant_names || [];
      const storedMode = notification.audience_mode;
      const resolvedMode = ['all', 'single', 'sheet'].includes(storedMode) ? storedMode : (targetNames.length ? 'sheet' : 'all');
      this.audienceMode = resolvedMode;
      if (resolvedMode === 'single') {
        const stored = targetNames[0] || '';
        this.singleTenantName = stored ? stored.split('.')[0] : '';
        this.sheetTenantNames = [];
      } else if (resolvedMode === 'sheet') {
        this.singleTenantName = '';
        this.sheetTenantNames = targetNames.slice();
      } else {
        this.singleTenantName = '';
        this.sheetTenantNames = [];
      }
      this.sheetError = '';
      this.addDataSideBar = true;
    },
    closeForm() {
      this.addDataSideBar = false;
      this.isReadOnly = false;
      this.resetForm();
    },
    resetForm() {
      this.form = {
        id: null,
        title: '',
        text: '',
        environment: '',
        video_url: '',
        file_url: '',
        file_name: '',
        scheduled_at: '',
        target_new_pricing_only: false,
        created_by_name: '',
        created_by_ip: '',
      };
      this.audienceMode = 'all';
      this.singleTenantName = '';
      this.sheetTenantNames = [];
      this.sheetError = '';
    },
    formatDateTime(dateString) {
      if (!dateString) return '-';
      try {
        const date = new Date(dateString.replace(' ', 'T'));
        if (Number.isNaN(date.getTime())) {
          return dateString;
        }
        const day     = String(date.getDate()).padStart(2, '0');
        const month   = String(date.getMonth() + 1).padStart(2, '0');
        const year    = date.getFullYear();
        const hours   = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${day}/${month}/${year} ${hours}:${minutes}`;
      } catch (exception) {
        return dateString;
      }
    },
    formatDateForInput(dateString) {
      if (!dateString) return '';
      try {
        const [datePart, timePart] = dateString.split(' ');
        if (!datePart) return '';
        const [year, month, day] = datePart.split('-');
        const time = timePart ? timePart.slice(0, 5) : '00:00';
        return `${day}/${month}/${year} ${time}`;
      } catch (exception) {
        return '';
      }
    },
    formatDateTimeForApi(dateString) {
      if (!dateString) return null;
      try {
        const [datePart, timePart] = dateString.split(' ');
        if (!datePart) return null;
        const [day, month, year] = datePart.split('/');
        const time = timePart || '00:00';
        return `${year}-${month}-${day} ${time}:00`;
      } catch (exception) {
        return dateString;
      }
    },
    async ensureTenantLoaded() {
      if (!this.selectedTenantName) { await this.loadDefaultTenant(); }
      return !!this.selectedTenantName;
    },
    fileExtension(file) {
      const name = file && file.name ? String(file.name) : '';
      const match = name.match(/\.([a-z0-9]+)$/i);
      return match ? match[1].toLowerCase() : '';
    },
    extractErrorMessage(exception, fallback) {
      const responseData = exception && exception.response && exception.response.data;
      if (typeof responseData === 'string' && responseData.trim()) return responseData;
      if (responseData && responseData.message) return responseData.message;
      if (exception && exception.message) return exception.message;
      return fallback;
    },
    triggerInput(refName) {
      const input = this.$refs[refName];
      if (input) input.click();
    },
    async uploadMedia(file) {
      const response = await fileRequests.upload(file, this.selectedTenantName);
      const data = (response && response.data) || {};
      return data.fileUrl || data.file_url || '';
    },
    async handleImageChange(event) {
      const file = event.target.files && event.target.files[0];
      event.target.value = '';
      if (!file) return;
      const extension = this.fileExtension(file);
      if (!this.mediaExtensions.includes(extension)) {
        this.$vs.notify({
          color: 'warning',
          title: 'Formato inválido',
          text: `Selecione uma imagem ou vídeo (${this.mediaExtensions.join(', ')}).`,
        });
        return;
      }
      if (!(await this.ensureTenantLoaded())) {
        this.$vs.notify({ color: 'warning', title: 'Aviso', text: 'Nenhum cliente ativo encontrado.' });
        return;
      }
      this.isUploadingImage = true;
      try {
        const url = await this.uploadMedia(file);
        if (!url) throw new Error('Upload sem URL');
        this.form.video_url = url;
      } catch (exception) {
        console.error(exception);
        this.$vs.notify({ color: 'danger', title: 'Erro!', text: this.extractErrorMessage(exception, 'Falha ao enviar mídia.') });
      } finally {
        this.isUploadingImage = false;
      }
    },
    isImageMedia(value) {
      if (!value || typeof value !== 'string') return false;
      const withoutQuery = value.split('?')[0];
      return /\.(png|jpe?g|gif|webp|svg|bmp)$/i.test(withoutQuery);
    },
    async handleFileChange(event) {
      const file = event.target.files && event.target.files[0];
      event.target.value = '';
      if (!file) return;
      const extension = this.fileExtension(file);
      if (!this.documentExtensions.includes(extension)) {
        return this.$vs.notify({
          color: 'warning',
          title: 'Formato inválido',
          text: `Selecione um documento (${this.documentExtensions.join(', ')}). Use o campo "Imagem ou vídeo" para mídia.`,
        });
      }
      if (!(await this.ensureTenantLoaded())) {
        this.$vs.notify({ color: 'warning', title: 'Aviso', text: 'Nenhum cliente ativo encontrado.' });
        return;
      }
      this.isUploadingFile = true;
      try {
        const url = await this.uploadMedia(file);
        if (!url) throw new Error('Upload sem URL');
        this.form.file_url = url;
        this.form.file_name = file.name;
      } catch (exception) {
        console.error(exception);
        this.$vs.notify({ color: 'danger', title: 'Erro!', text: this.extractErrorMessage(exception, 'Falha ao enviar arquivo.') });
      } finally {
        this.isUploadingFile = false;
      }
    },
    clearMedia() {
      this.form.video_url = '';
    },
    clearFile() {
      this.form.file_url = '';
      this.form.file_name = '';
    },
    clearSheet() {
      this.sheetTenantNames = [];
      this.sheetError = '';
      this.sheetResetKey += 1;
    },
    onSheetParsed(excelData) {
      this.sheetError = '';
      const rows = (excelData && excelData.results) || [];
      const headers = (excelData && excelData.header) || [];
      const columnName = this.resolveTenantNameHeader(headers);
      if (!columnName) {
        this.sheetError = 'A primeira linha da planilha precisa ter uma coluna chamada "nome".';
        this.sheetTenantNames = [];
        this.sheetResetKey += 1;
        return;
      }
      const seen = new Set();
      const names = [];
      rows.forEach((row) => {
        const value = row[columnName];
        const normalized = (value === undefined || value === null) ? '' : String(value).trim();
        if (!normalized || seen.has(normalized.toLowerCase())) return;
        seen.add(normalized.toLowerCase());
        names.push(normalized);
      });
      if (!names.length) {
        this.sheetError = 'Nenhum cliente válido encontrado na planilha.';
        this.sheetTenantNames = [];
        return;
      }
      if (names.length > this.maxSheetRows) {
        this.sheetError = `Máximo de ${this.maxSheetRows} clientes por planilha.`;
        this.sheetTenantNames = [];
        this.sheetResetKey += 1;
        return;
      }
      const known = this.availableTenantFqdnSet;
      const lookup = this.tenantLookupBySlug;
      const resolved = [];
      const unknown = [];
      names.forEach((name) => {
        const trimmedLower = name.toLowerCase();
        if (known.has(name)) { resolved.push(name); return; }
        if (lookup[trimmedLower]) { resolved.push(lookup[trimmedLower]); return; }
        unknown.push(name);
      });
      if (known.size && unknown.length) {
        this.sheetError = `${unknown.length} cliente(s) não reconhecido(s): ${unknown.join(', ')}`;
        this.sheetTenantNames = [];
        this.sheetResetKey += 1;
        return;
      }
      this.sheetTenantNames = resolved;
    },
    resolveTenantNameHeader(headers) {
      if (!Array.isArray(headers)) return null;
      for (const header of headers) {
        const normalized = String(header || '').trim().toLowerCase();
        if (this.sheetNameColumns.includes(normalized)) return header;
      }
      return null;
    },
    buildTargetTenantNames() {
      if (this.audienceMode === 'single') {
        const trimmed = (this.singleTenantName || '').trim();
        if (!trimmed) return [];
        const resolved = this.tenantLookupBySlug[trimmed.toLowerCase()] || trimmed;
        return [resolved];
      }
      if (this.audienceMode === 'sheet') {
        return this.sheetTenantNames.slice();
      }
      return [];
    },
    buildPayload() {
      return {
        title: this.form.title ? encodeToBase64(this.form.title) : '',
        text: this.form.text ? encodeToBase64(this.form.text) : '',
        environment: this.form.environment || null,
        video_url: this.form.video_url || null,
        file_url: this.form.file_url || null,
        file_name: this.form.file_name || null,
        scheduled_at: this.form.scheduled_at ? this.formatDateTimeForApi(this.form.scheduled_at) : null,
        target_tenant_names: this.buildTargetTenantNames(),
        target_new_pricing_only: this.audienceMode === 'single' ? false : !!this.form.target_new_pricing_only,
        audience_mode: this.audienceMode,
      };
    },
    async submitForm() {
      await this.$validator.validateAll().then(async valid => {
        if (!valid) {
          this.$vs.notify({
            color: 'warning',
            title: 'Aviso',
            text: 'Preencha todos os campos obrigatórios.',
          });

          return;
        }

        if (this.audienceMode === 'single' && !this.singleTenantName.trim()) {
          this.$vs.notify({ color: 'warning', title: 'Aviso', text: 'Selecione um cliente.' });
          return;
        }
        if (this.audienceMode === 'sheet' && !this.sheetTenantNames.length) {
          this.$vs.notify({ color: 'warning', title: 'Aviso', text: 'Importe uma planilha com clientes.' });
          return;
        }

        if (!(await this.ensureTenantLoaded())) {
          this.$vs.notify({
            color: 'warning',
            title: 'Aviso',
            text: 'Nenhum cliente ativo encontrado para autenticação.',
          });
          return;
        }

        this.isSaving = true;
        const payload = this.buildPayload();

        try {
          if (this.formMode === 'create') {
            await featureNotification.createNotification(this.token, payload, this.selectedTenantName);
            this.$vs.notify({
              color: 'success',
              title: 'Sucesso!',
              text: 'Notificação criada com sucesso.',
            });
          } else if (this.form.id) {
            await featureNotification.updateNotification(this.token, this.form.id, payload, this.selectedTenantName);
            this.$vs.notify({
              color: 'success',
              title: 'Sucesso!',
              text: 'Notificação atualizada com sucesso.',
            });
          }
          this.addDataSideBar = false;
          this.resetForm();
          await this.fetchNotifications();
        } catch (exception) {
          console.error(exception);
          this.$vs.notify({
            color: 'danger',
            title: 'Erro!',
            text: 'Não foi possível salvar a notificação. Tente novamente.',
          });
        } finally {
          this.isSaving = false;
        }
      });
    },
    async fetchNotifications() {
      if (!(await this.ensureTenantLoaded())) {
        this.notifications = [];
        this.$vs.notify({
          color: 'warning',
          title: 'Aviso',
          text: 'Nenhum cliente ativo encontrado para buscar notificações.',
        });
        return;
      }

      try {
        const response = await featureNotification.fetchNotifications(this.token, this.selectedTenantName);
        this.notifications = response && response.data ? response.data : [];
      } catch (exception) {
        console.error(exception);
        this.notifications = [];
        this.$vs.notify({
          color: 'danger',
          title: 'Erro!',
          text: 'Não foi possível carregar as notificações.',
        });
      }
    },
    promptCancel(notification) {
      this.confirmDialog = {
        active: true,
        target: notification,
      };
    },
    openDetails(notification) {
      this.openEdit(notification);
      this.isReadOnly = true;
    },
    async confirmCancelAction() {
      const target = this.confirmDialog.target;
      if (!target || !target.id) {
        this.confirmDialog.active = false;
        return;
      }
      if (!(await this.ensureTenantLoaded())) {
        this.$vs.notify({
          color: 'warning',
          title: 'Aviso',
          text: 'Nenhum cliente ativo encontrado para atualizar a notificação.',
        });
        this.confirmDialog.active = false;
        return;
      }
      const desiredState = !target.is_cancelled;
      try {
        await featureNotification.updateNotification(this.token, target.id, { is_cancelled: desiredState }, this.selectedTenantName);
        this.$vs.notify({
          color: 'success',
          title: 'Sucesso!',
          text: `Notificação ${desiredState ? 'cancelada' : 'reativada'} com sucesso.`,
        });
        await this.fetchNotifications();
      } catch (exception) {
        console.error(exception);
        this.$vs.notify({
          color: 'danger',
          title: 'Erro!',
          text: 'Não foi possível atualizar o status da notificação.',
        });
      } finally {
        this.confirmDialog = {
          active: false,
          target: null,
        };
      }
    },
  },
  created() {
    this.loadDefaultTenant().then(() => this.fetchNotifications());
  },
  mounted() {
    this.isMounted = true;
  }
}
</script>

<style lang="scss">

.align-self-center{
  align-self: center !important;
}

.feature-notification-popup .vs-popup {
  width: 680px;
  max-width: calc(100vw - 32px);
}

.feature-notification-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;

  .fn-hidden-input { display: none; }

  .fn-field { display: flex; flex-direction: column; gap: 0.375rem; }

  .fn-row { display: flex; gap: 1rem; flex-wrap: wrap; }
  .fn-col { flex: 1 1 0; min-width: 220px; }

  .fn-label {
    font-size: 0.75rem;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.6);
    letter-spacing: 0.02em;
  }

  .fn-divider { height: 1px; background: rgba(0, 0, 0, 0.08); margin: 0.25rem 0; }

  .fn-radio-group { display: flex; gap: 1.25rem; flex-wrap: wrap; }

  .fn-audience {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.5rem;
  }

  .fn-audience__option {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    padding: 0.625rem 0.75rem;
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 8px;
    cursor: pointer;
    transition: border-color 0.15s, background 0.15s;

    &:hover { border-color: rgba(var(--vs-primary), 0.5); }
  }

  .fn-audience__option--active {
    border-color: rgba(var(--vs-primary), 1);
    background: rgba(var(--vs-primary), 0.05);
  }

  .fn-audience__option--disabled { cursor: not-allowed; opacity: 0.6; }

  .fn-audience__info { display: flex; flex-direction: column; gap: 0.125rem; min-width: 0; }

  .fn-audience__title {
    font-size: 0.9rem;
    font-weight: 500;
    color: rgba(0, 0, 0, 0.85);
  }

  .fn-audience__hint {
    font-size: 0.75rem;
    color: rgba(0, 0, 0, 0.55);
    line-height: 1.3;
  }

  .fn-filter {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.75rem 1rem;
    background: rgba(0, 0, 0, 0.025);
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 8px;
  }

  .fn-filter__text { display: flex; flex-direction: column; gap: 0.125rem; }

  .fn-filter__title {
    font-size: 0.9rem;
    font-weight: 500;
    color: rgba(0, 0, 0, 0.85);
  }

  .fn-filter__hint {
    font-size: 0.75rem;
    color: rgba(0, 0, 0, 0.55);
    line-height: 1.3;
  }

  .fn-switch { display: flex; align-items: center; gap: 0.75rem; }

  .fn-media-cell { display: flex; align-items: center; min-height: 38px; }

  .fn-media-preview {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.375rem;
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 6px;
    background: rgba(0, 0, 0, 0.02);

    img {
      width: 56px;
      height: 56px;
      object-fit: cover;
      border-radius: 4px;
    }
  }

  .fn-media-actions { display: flex; gap: 0.25rem; }

  .fn-media-file {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 0.75rem;
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 6px;
    background: rgba(0, 0, 0, 0.02);
  }

  .fn-media-filename {
    color: rgba(var(--vs-primary), 1);
    font-weight: 500;
    word-break: break-all;
    flex: 1;
  }

  .fn-media-hint { font-size: 0.8rem; color: rgba(0, 0, 0, 0.55); margin: 0; }

  .fn-inline-link { margin-left: 0.5rem; color: rgba(var(--vs-danger), 1); cursor: pointer; text-decoration: underline; }

  .fn-error { color: rgba(var(--vs-danger), 1); font-size: 0.85rem; margin: 0; }

  .fn-footer { display: flex; justify-content: flex-end; gap: 0.5rem; margin-top: 0.5rem; }

  .excel-import > div {
    padding: 1.25rem 1rem !important;
    font-size: 0.95rem !important;
  }

  .excel-import .feather { width: 32px !important; height: 32px !important; }
}

.notifications-list {
  .segmentation-cell {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.25rem;
    width: 100%;
  }

  .segmentation-cell__row {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    width: 100%;
  }

  .segmentation-text--truncate {
    display: block;
    max-width: 220px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .segmentation-text--expanded {
    max-width: 320px;
    white-space: normal;
    word-break: break-word;
    text-align: center;
  }

  .segmentation-cell__toggle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    width: 22px;
    height: 22px;
    border: 0;
    background: transparent;
    color: rgba(var(--vs-primary), 1);
    cursor: pointer;
    padding: 0;

    &:focus { outline: none; }

    i { font-size: 18px !important; }
  }

  .vs-con-table {
    .product-name {
      max-width: 23rem;
    }

    .vs-table--header {
      display: flex;
      flex-wrap: wrap-reverse;
      margin-left: 1.5rem;
      margin-right: 1.5rem;

      >span {
        display: flex;
        flex-grow: 1;
      }

      .vs-table--search {
        padding-top: 0;

        .vs-table--search-input {
          padding: 0.9rem 2.5rem;
          font-size: 1rem;

          &+i {
            left: 1rem;
          }

          &:focus+i {
            left: 1rem;
          }
        }
      }
    }

    .vs-table {
      border-collapse: separate;
      border-spacing: 0 1.3rem;
      padding: 0 1rem;

      tr {
        box-shadow: 0 4px 20px 0 rgba(0, 0, 0, .05);

        td {
          padding: 10px;
          text-align: center;
          &:first-child {
            border-top-left-radius: .5rem;
            border-bottom-left-radius: .5rem;
          }

          &:last-child {
            border-top-right-radius: .5rem;
            border-bottom-right-radius: .5rem;
          }

          &.img-container {
            span {
              display: flex;
              justify-content: flex-start;
            }

            .product-img {
              height: 110px;
            }
          }
        }

        td.td-check {
          padding: 20px !important;
        }
        td.td.vs-table--td.actionTD > span {
          display: flex;
          justify-content: center;
        }
      }
    }

    .vs-table--thead {
      th {
        padding-top: 0;
        padding-bottom: 0;

        .vs-table-text {
          text-transform: uppercase;
          font-weight: 600;
          justify-content: center !important;
        }
      }

      th.td-check {
        padding: 0 15px !important;
      }

      tr {
        background: none;
        box-shadow: none;
      }
    }

    .vs-table--pagination {
      justify-content: center;
    }
  }
}
</style>
